import { inject, Injectable, InjectionToken } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { ToastController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { PanelHostService } from '../panel-host.service';
import { StripePaymentService } from '../stripe-payment.service';
import {
  decideUpdateAction,
  PWA_UPDATE_POLL_INTERVAL_MS,
} from './decide-update-action';
import {
  presentLoadFailedToast,
  presentUpdateAvailableToast,
} from './pwa-update-toasts';

export const PWA_RELOAD = new InjectionToken<() => void>('PWA_RELOAD', {
  providedIn: 'root',
  factory: () => () => window.location.reload(),
});

const UNRECOVERABLE_RELOAD_KEY = 'pwa-unrecoverable-reloaded';

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly router = inject(Router);
  private readonly panelHost = inject(PanelHostService);
  private readonly stripePayment = inject(StripePaymentService);
  private readonly toastController = inject(ToastController);
  private readonly transloco = inject(TranslocoService);
  private readonly reload = inject(PWA_RELOAD);

  private updateReadyAt: number | null = null;
  private toastShown = false;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;

  initialize(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.updateReadyAt = Date.now();
        this.toastShown = false;
      }
    });

    this.swUpdate.unrecoverable.subscribe(() => {
      void this.handleUnrecoverable();
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        void this.evaluate();
      });

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        void this.checkForUpdate();
        this.startPollInterval();
        void this.evaluate();
      } else {
        this.clearPollInterval();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    if (document.visibilityState === 'visible') {
      this.startPollInterval();
    }
  }

  private startPollInterval(): void {
    this.clearPollInterval();
    this.pollIntervalId = setInterval(() => {
      void this.checkForUpdate();
      void this.evaluate();
    }, PWA_UPDATE_POLL_INTERVAL_MS);
  }

  private clearPollInterval(): void {
    if (this.pollIntervalId !== null) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // Offline — ngsw throws; ignore silently.
    }
  }

  private async evaluate(): Promise<void> {
    const action = decideUpdateAction({
      updateReady: this.updateReadyAt !== null,
      panelOpen: this.panelHost.activePanel() !== null,
      stripeInFlight: this.stripePayment.stripePaymentInFlight(),
      msSinceUpdateReady:
        this.updateReadyAt === null ? null : Date.now() - this.updateReadyAt,
      toastShown: this.toastShown,
    });

    if (action === 'reload') {
      await this.applyUpdate();
      return;
    }

    if (action === 'show-toast') {
      this.toastShown = true;
      await presentUpdateAvailableToast(
        this.transloco,
        this.toastController,
        () => {
          void this.applyUpdate();
        },
      );
    }
  }

  private async applyUpdate(): Promise<void> {
    if (await this.swUpdate.activateUpdate()) {
      this.reload();
    }
  }

  private async handleUnrecoverable(): Promise<void> {
    if (sessionStorage.getItem(UNRECOVERABLE_RELOAD_KEY)) {
      await presentLoadFailedToast(this.transloco, this.toastController);
      return;
    }

    sessionStorage.setItem(UNRECOVERABLE_RELOAD_KEY, '1');
    this.reload();
  }
}
